import Dexie, { type EntityTable } from 'dexie'
import { lineTotal, QTY_UNIT, type Vnd } from './money'

/**
 * Cơ sở dữ liệu ngay trên máy. Bán hàng ghi vào đây trước, đẩy lên máy chủ sau.
 * Quán ở chợ rớt mạng liên tục — mất mạng mà không bán được là mất khách ngay ngày đầu.
 */

export type OrderStatus = 'Open' | 'Paid' | 'Void'
/**
 * Ba cách đóng một đơn.
 *
 * `Debt` là ghi sổ — khách quen mang đồ về trước, tiền trả sau. Vẫn là một lần bán trọn
 * vẹn: đơn đóng lại, doanh thu ghi ngay hôm bán. Chỉ có tiền là chưa vào két, nên mọi chỗ
 * đếm tiền mặt phải bỏ nó ra.
 */
export type PayMethod = 'Cash' | 'Transfer' | 'Debt'
export type DiscountKind = 'Percent' | 'Amount'

/**
 * Khách quen được ghi sổ.
 *
 * Quán nào cũng có vài người: hàng xóm, mối sỉ, mấy anh công trình bên cạnh. Trước đây
 * nhân viên ghi tên vào tờ giấy dán ở quầy, mà tờ giấy ấy rách là không ai chứng minh
 * được gì.
 *
 * Không giữ sẵn số dư. Nó tính lại mỗi lần đọc, bằng tổng đơn ghi sổ trừ tổng tiền đã trả
 * — lưu sẵn thì phải nhớ cập nhật ở năm chỗ, quên một chỗ là con số ấy nói dối mãi.
 */
export interface Customer {
  id: string
  name: string
  phone: string | null
  note: string | null
  /** Trần nợ, VND. 0 là quán không đặt trần cho người này. */
  debtLimit: Vnd
}

/**
 * Một lần khách trả bớt nợ.
 *
 * Dòng riêng chứ không sửa vào đơn cũ: khách nợ mười đơn rồi đưa một cục tiền là chuyện
 * thường, mà chia cục tiền ấy ra mười tờ bill thì không ai làm đúng được.
 */
export interface DebtPayment {
  id: string
  customerId: string
  amount: Vnd
  method: PayMethod
  paidAt: string
  note: string | null
  deviceId: string
  synced: 0 | 1
}

export interface Category {
  id: string
  name: string
  sortOrder: number
}

export interface Product {
  id: string
  categoryId: string | null
  name: string
  price: Vnd
  unit: string
  quickOptions: string[]
  soldByWeight: boolean
  /** Mã in trên bao bì. Món tự pha như cà phê thì để trống. */
  barcode: string | null
  /** Mã ảnh: mã kho dựng sẵn, hoặc "u/..." nếu chủ quán tự tải lên. */
  imageKey: string | null
  /** Những bộ tuỳ chọn áp cho món này. Rỗng nghĩa là món không có ghi chú đặt sẵn. */
  optionSetIds: string[]
  sortOrder: number
}

/**
 * Một bộ ghi chú đặt sẵn: "Đường" gồm ít đường / bình thường / nhiều đường.
 *
 * Khác `quickOptions` của món, và đừng lẫn hai thứ: `quickOptions` là biến thể in lên mặt
 * nút gọi món (M/L, Đá/Nóng) và quyết định đây là dòng hàng nào. Bộ này là ghi chú, chọn
 * sau khi món đã vào giỏ, không đổi giá và không tách dòng.
 */
export interface OptionSet {
  id: string
  name: string
  choices: string[]
  sortOrder: number
}

export interface Zone {
  id: string
  name: string
  sortOrder: number
}

export interface DiningTable {
  id: string
  zoneId: string
  name: string
  sortOrder: number
  isTakeaway: boolean
  icon: string | null
  color: string | null
  gridX: number
  gridY: number
  gridW: number
  gridH: number
  seats: number
  reservedAt: string | null
  reservedName: string | null
  /** Số của người đặt. Chỉ hiện bốn số cuối trên sơ đồ bàn — cả quán nhìn thấy màn hình đó. */
  reservedPhone: string | null
  reservedPartySize: number
  reservedNote: string | null
}

/**
 * Chương trình khuyến mãi đặt sẵn thành nút bấm ở màn hình thu tiền.
 * Máy chủ là nguồn sự thật — kéo về cùng danh mục để mất mạng vẫn bấm được.
 */
export interface Promotion {
  id: string
  name: string
  kind: DiscountKind
  /** Phần trăm (1–100) hoặc số tiền VND, tuỳ `kind`. */
  value: number
  /** Hoá đơn phải từ ngần này trở lên. 0 là không ràng buộc. */
  minOrderTotal: Vnd
  /** Trần số tiền bớt khi tính theo phần trăm. 0 là không chặn trần. */
  maxDiscount: Vnd
  /** YYYY-MM-DD theo ngày Việt Nam, hoặc null nếu chạy vô thời hạn. */
  startsOn: string | null
  endsOn: string | null
  sortOrder: number
}

export interface ExpenseCategory {
  id: string
  name: string
  emoji: string
  sortOrder: number
}

export interface OrderItem {
  id: string
  productId: string | null
  name: string
  unitPrice: Vnd
  qtyMilli: number
  lineTotal: Vnd
  optionLabel: string | null
  note: string | null
}

/**
 * Kết quả hoá đơn điện tử của một đơn, chụp lại ngay trên máy.
 *
 * Giữ ở đây để in lại tờ bill lúc mất mạng vẫn ra đúng số hoá đơn và mã cơ quan thuế —
 * khách quay lại xin bản in thì không ai đi hỏi máy chủ mới in được.
 *
 * Máy khách <b>không bao giờ tự điền mấy ô này</b>. Chúng chỉ đến từ máy chủ, vì số hoá
 * đơn là thứ nhà cung cấp cấp phát, không phải thứ máy quầy tự nghĩ ra.
 */
export interface OrderEInvoice {
  status: 'Pending' | 'Issued' | 'Failed' | 'Cancelled'
  templateCode: string | null
  series: string | null
  invoiceNo: string | null
  taxAuthorityCode: string | null
  lookupUrl: string | null
  issuedAt: string | null
  error: string | null
}

export interface Order {
  id: string
  code: string
  tableId: string | null
  tableName: string | null
  status: OrderStatus
  openedAt: string
  paidAt: string | null
  subtotal: Vnd
  discount: Vnd
  /** Số tiền phí phục vụ đã cộng vào đơn này. */
  serviceFee: Vnd
  /** Tỷ lệ phí phục vụ lúc bán, phần nghìn. 50 = 5,0%. Chụp lại để in bill sau vẫn đúng. */
  serviceFeePermille: number
  /** Số khách ngồi bàn cho đơn này. 0 nghĩa là chưa ai đếm. */
  guests: number
  /**
   * Lúc bếp báo làm xong. Đơn chuyển sang "chờ khách lấy".
   *
   * Không dính gì tới tiền: khách mang về trả tiền trước rồi mới đứng đợi pha.
   */
  readyAt: string | null
  /** Lúc giao tận tay khách, hoặc lúc dọn bàn. Xong hẳn thì ô bàn mới trống. */
  doneAt: string | null
  /** Câu in lên bill: "Giảm 10%" hoặc tên chương trình khuyến mãi. */
  discountLabel: string | null
  /** Phần trăm đã bớt, 0 nghĩa là bớt thẳng một số tiền. Thêm món thì số tiền bớt chạy theo. */
  discountPercent: number
  promotionId: string | null
  total: Vnd
  paymentMethod: PayMethod | null
  amountGiven: Vnd
  changeGiven: Vnd
  customerName: string | null
  /** Khách quen đứng tên đơn ghi sổ này. Đơn trả tiền ngay thì để trống. */
  customerId: string | null
  note: string | null
  deviceId: string
  clientCreatedAt: string
  items: OrderItem[]
  /** Huỷ hoá đơn đã thu tiền: không xoá dòng, không sửa số tiền, chỉ đánh dấu. */
  voidedAt: string | null
  voidReason: string | null
  /** Do máy chủ trả về, không phải do máy này tự điền. Trống nghĩa là quán chưa bật. */
  einvoice?: OrderEInvoice
  /** 0 = chưa đẩy lên máy chủ. Dexie không đánh chỉ mục được kiểu boolean. */
  synced: 0 | 1
}

export interface Expense {
  id: string
  categoryId: string | null
  categoryName: string
  emoji: string
  amount: Vnd
  note: string | null
  photoUrl: string | null
  spentAt: string
  deviceId: string
  /** Chỉ khoản trả tiền mặt mới rút két, nên chốt ca mới trừ đúng. */
  paymentMethod: PayMethod
  synced: 0 | 1
}

export interface Shift {
  id: string
  openedAt: string
  closedAt: string | null
  openingCash: Vnd
  cashSales: Vnd
  cashExpense: Vnd
  expectedCash: Vnd
  countedCash: Vnd
  cashDiff: Vnd
  note: string | null
  deviceId: string
  synced: 0 | 1
}

export interface KvRow {
  key: string
  value: unknown
}

class PosDb extends Dexie {
  kv!: EntityTable<KvRow, 'key'>
  categories!: EntityTable<Category, 'id'>
  products!: EntityTable<Product, 'id'>
  zones!: EntityTable<Zone, 'id'>
  diningTables!: EntityTable<DiningTable, 'id'>
  expenseCategories!: EntityTable<ExpenseCategory, 'id'>
  promotions!: EntityTable<Promotion, 'id'>
  optionSets!: EntityTable<OptionSet, 'id'>
  orders!: EntityTable<Order, 'id'>
  expenses!: EntityTable<Expense, 'id'>
  shifts!: EntityTable<Shift, 'id'>
  customers!: EntityTable<Customer, 'id'>
  debtPayments!: EntityTable<DebtPayment, 'id'>

  constructor() {
    super('sigopos')
    this.version(7).stores({
      kv: 'key',
      categories: 'id, sortOrder',
      products: 'id, categoryId, barcode, sortOrder',
      zones: 'id, sortOrder',
      diningTables: 'id, zoneId, sortOrder',
      expenseCategories: 'id, sortOrder',
      promotions: 'id, sortOrder',
      optionSets: 'id, sortOrder',
      orders: 'id, status, tableId, paidAt, synced, openedAt',
      expenses: 'id, spentAt, synced',
      shifts: 'id, openedAt, closedAt, synced',
      customers: 'id, name',
      debtPayments: 'id, customerId, paidAt, synced',
    })
  }
}

export const db = new PosDb()

// ───────────────────────── kv ─────────────────────────

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const row = await db.kv.get(key)
  return row?.value as T | undefined
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await db.kv.put({ key, value })
}

/**
 * Mã máy. Đi vào số hoá đơn để hai máy cùng quán bán offline không đụng số nhau,
 * và để tra ra đơn này bán ở máy nào khi cần đối chiếu.
 */
export async function deviceId(): Promise<string> {
  let id = await kvGet<string>('deviceId')
  if (!id) {
    id = crypto.randomUUID().slice(0, 8).toUpperCase()
    await kvSet('deviceId', id)
  }
  return id
}

// ───────────────────────── đơn hàng ─────────────────────────

const two = (n: number) => String(n).padStart(2, '0')

/** HD260908-A1F0042 — ngày + mã máy + số thứ tự trong ngày của chính máy đó. */
export async function nextOrderCode(): Promise<string> {
  const dev = await deviceId()
  const now = new Date()
  const stamp = `${String(now.getFullYear()).slice(2)}${two(now.getMonth() + 1)}${two(now.getDate())}`

  const key = `seq:${stamp}`
  const current = (await kvGet<number>(key)) ?? 0
  const next = current + 1
  await kvSet(key, next)

  return `HD${stamp}-${dev.slice(0, 3)}${String(next).padStart(4, '0')}`
}

export const recalc = (items: OrderItem[]): Vnd =>
  items.reduce((sum, i) => sum + i.lineTotal, 0)

// ───────────────────────── phí phục vụ ─────────────────────────

/*
 * Phí phục vụ: quán cộng thêm mấy phần trăm vào hoá đơn.
 *
 * Ba quy tắc, và cả ba đều có lý do bằng tiền:
 *
 *   1. Tính trên số tiền khách <b>thật sự phải trả</b>, tức là sau khi đã bớt. Thu phí
 *      trên phần đã giảm là bắt khách trả phí cho món tiền họ không đưa.
 *   2. Tỷ lệ được chụp lại vào từng đơn. Chủ quán đổi mức thu giữa tháng thì tờ bill in
 *      lại hôm sau vẫn phải nói đúng hôm ấy thu mấy phần trăm.
 *   3. Đơn đang mở thì tỷ lệ chạy theo cài đặt hiện tại của quán. Chủ quán bật phí lên
 *      lúc 11 giờ mà mấy bàn đang ngồi vẫn tính kiểu cũ thì hai bàn cạnh nhau ra hai
 *      cách tính khác nhau, và người phải giải thích là người bán.
 */

/** Quán không khai thì bằng 0, và khi đó không có dòng phí nào hiện ra ở đâu cả. */
const MAX_SERVICE_PERMILLE = 300

/**
 * Tỷ lệ phí phục vụ của quán, phần nghìn.
 *
 * Đọc từ gói thông tin quán đã nằm sẵn trên máy, không hỏi máy chủ: tính tiền là việc
 * phải chạy được lúc mất mạng.
 */
async function shopServicePermille(): Promise<number> {
  const shop = await kvGet<{ serviceChargePermille?: number }>('shop')
  const v = Math.round(shop?.serviceChargePermille ?? 0)
  if (!Number.isFinite(v) || v <= 0) return 0
  return Math.min(v, MAX_SERVICE_PERMILLE)
}

/** Số tiền phí phục vụ của một hoá đơn. */
export const serviceFeeOf = (subtotal: Vnd, discount: Vnd, permille: number): Vnd =>
  permille > 0 ? Math.round((Math.max(0, subtotal - discount) * permille) / 1000) : 0

/** "5%" · "3,5%" — câu đứng cạnh số tiền phí, trên màn hình lẫn trên giấy. */
export const servicePercentText = (permille: number): string =>
  (permille / 10).toLocaleString('vi-VN', { maximumFractionDigits: 1 })

/** Chỉ cần id và tên bàn — nhận hẹp lại để nơi gọi khỏi phải dựng cả đối tượng bàn. */
export type TableRef = Pick<DiningTable, 'id' | 'name'>

/**
 * Mở đơn cho một bàn. Bàn đang có đơn dở thì vào tiếp đơn ấy.
 *
 * Đúng với bàn ngồi: một mâm khách là một tờ bill, chạm vào bàn giữa bữa là để gọi thêm
 * món chứ không phải mở tờ thứ hai.
 */
export async function openOrderForTable(table: TableRef): Promise<Order> {
  const existing = await db.orders.where({ tableId: table.id, status: 'Open' }).first()
  if (existing) return existing
  return startOrderForTable(table)
}

/**
 * Đơn của màn hình Bán nhanh.
 *
 * Không vồ lấy đơn đang dở ở quầy mang về: chỗ đó giờ có thể đang xếp hàng, mỗi người một
 * đơn một số gọi, cướp đơn của người trước là ly của người sau rơi vào bill người trước.
 *
 * Vẫn nhặt lại cái vỏ đơn rỗng mà lần mở màn hình trước bỏ lại, để ra vào Bán nhanh mấy
 * lượt không rải đầy đơn trắng trong sổ.
 */
export async function quickOrderForTable(table: TableRef): Promise<Order> {
  const blank = await db.orders
    .where({ tableId: table.id, status: 'Open' })
    .filter((o) => o.items.length === 0)
    .first()
  return blank ?? startOrderForTable(table)
}

/**
 * Mở một đơn <b>mới toanh</b>, kể cả khi bàn đang có đơn dở.
 *
 * Dành cho quầy mang về: khách sau đứng nối đuôi khách trước, hai người là hai đơn riêng
 * với hai số gọi riêng. Dùng chung `openOrderForTable` ở đây là ly của người sau rơi vào
 * bill của người trước.
 */
export async function startOrderForTable(table: TableRef): Promise<Order> {
  const now = new Date().toISOString()
  const order: Order = {
    id: crypto.randomUUID(),
    code: await nextOrderCode(),
    tableId: table.id,
    tableName: table.name,
    status: 'Open',
    openedAt: now,
    paidAt: null,
    subtotal: 0,
    discount: 0,
    serviceFee: 0,
    serviceFeePermille: await shopServicePermille(),
    guests: 0,
    readyAt: null,
    doneAt: null,
    discountLabel: null,
    discountPercent: 0,
    promotionId: null,
    total: 0,
    paymentMethod: null,
    customerId: null,
    amountGiven: 0,
    changeGiven: 0,
    customerName: null,
    note: null,
    deviceId: await deviceId(),
    clientCreatedAt: now,
    items: [],
    voidedAt: null,
    voidReason: null,
    synced: 0,
  }
  await db.orders.add(order)
  return order
}

/**
 * Số tiền bớt của một hoá đơn, sau khi đã chặn hai đầu.
 *
 * Bớt theo phần trăm thì tính lại theo tạm tính hiện tại, không giữ con số cũ: khách gọi
 * thêm hai ly nữa mà số tiền bớt đứng nguyên thì hoá đơn sai theo cách không ai để ý.
 */
export function resolveDiscount(
  subtotal: Vnd,
  discount: Vnd,
  discountPercent: number,
): Vnd {
  const raw = discountPercent > 0 ? Math.round((subtotal * discountPercent) / 100) : discount
  return Math.min(Math.max(0, raw), Math.max(0, subtotal))
}

/**
 * Tính lại số tiền bớt sau khi đơn đổi món.
 *
 * Đơn đang chạy một chương trình thì hỏi lại chính chương trình đó — có thế mới giữ đúng
 * trần và mức hoá đơn tối thiểu. Chương trình bị bỏ giữa chừng thì thôi, giữ nguyên số tiền
 * đã bớt: khách đã được hứa rồi, không rút lại giữa bữa.
 */
async function recalcDiscount(order: Order, subtotal: Vnd): Promise<Vnd> {
  if (order.promotionId) {
    const promo = await db.promotions.get(order.promotionId)
    if (promo) return promotionDiscount(promo, subtotal)
  }
  return resolveDiscount(subtotal, order.discount ?? 0, order.discountPercent ?? 0)
}

export async function saveItems(orderId: string, items: OrderItem[]): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order) return

  const subtotal = recalc(items)
  const discount = await recalcDiscount(order, subtotal)
  const permille = await shopServicePermille()
  const serviceFee = serviceFeeOf(subtotal, discount, permille)

  await db.orders.update(orderId, {
    items,
    subtotal,
    discount,
    // Bớt 0 đồng thì gỡ luôn cái nhãn. Còn chữ "Giảm 10%" trên bill mà không bớt đồng nào
    // là nói với khách một điều không đúng.
    discountLabel: discount > 0 ? (order.discountLabel ?? null) : null,
    serviceFee,
    serviceFeePermille: permille,
    total: subtotal - discount + serviceFee,
    synced: 0,
  })
}

/**
 * Tính lại phí phục vụ của một đơn <b>đang mở</b> theo mức quán đang khai.
 *
 * Cần cái này vì phí chỉ được tính lại mỗi khi đơn đổi món. Chủ quán bật phí lên lúc 11
 * giờ, mấy bàn đã ngồi từ 10 rưỡi mà không ai gọi thêm gì nữa thì tới lúc tính tiền vẫn
 * ra giá cũ — hai bàn cạnh nhau ra hai cách tính, và người phải đứng giải thích là người
 * bán. Gọi lúc mở màn hình gọi món và lúc mở màn hình thu tiền.
 *
 * Chỉ ghi khi con số thật sự đổi. Ghi vô điều kiện thì mỗi lần mở màn hình là một lần
 * đánh dấu "chưa đồng bộ", và hàng đợi đẩy lên đầy những đơn không có gì mới.
 */
export async function syncServiceFee(orderId: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status !== 'Open') return

  const permille = await shopServicePermille()
  const serviceFee = serviceFeeOf(order.subtotal, order.discount, permille)
  if (serviceFee === (order.serviceFee ?? 0) && permille === (order.serviceFeePermille ?? 0)) return

  await db.orders.update(orderId, {
    serviceFee,
    serviceFeePermille: permille,
    total: order.subtotal - order.discount + serviceFee,
    synced: 0,
  })
}

/** Số khách ngồi bàn. Không đụng tới tiền, nhưng vẫn phải đẩy lên cho máy kia thấy. */
export async function setGuests(orderId: string, guests: number): Promise<void> {
  await db.orders.update(orderId, { guests: Math.max(0, Math.round(guests)), synced: 0 })
}

/**
 * Tên khách đứng đợi lấy đồ.
 *
 * Quán đông, năm người cùng đứng chờ mang về thì "ly cà phê muối của ai đây" là câu hỏi
 * mất thời gian nhất quầy. Có tên thì gọi tên; chưa kịp hỏi tên thì gọi ba số cuối của mã
 * đơn, thứ đã in sẵn trên tờ bill. Ô này vốn đã đi lên máy chủ và in ra bill từ đầu —
 * chỉ là chưa bao giờ có chỗ để nhập vào.
 */
export async function setCustomerName(orderId: string, name: string | null): Promise<void> {
  const clean = name?.trim()
  await db.orders.update(orderId, { customerName: clean ? clean.slice(0, 60) : null, synced: 0 })
}

/*
 * Ba nấc phục vụ, chạy song song với tiền chứ không nối đuôi nó.
 *
 * Trước đây thu tiền xong là đơn biến khỏi sơ đồ bàn. Nhưng trả tiền không phải là xong
 * việc: ly cà phê mang về còn phải pha, và bàn ăn tại chỗ thì cả mâm khách vẫn đang ngồi
 * đó. Người bán mất dấu đúng những khách đang đứng trước mặt mình.
 *
 *   Đang làm  — vừa gọi món, bếp chưa báo gì
 *   Xong      — bếp báo xong, đang chờ khách lấy hoặc chờ bưng ra
 *   Đã giao   — giao tận tay khách, hoặc dọn bàn xong. Tới đây ô bàn mới trống.
 */
export type ServiceStage = 'making' | 'ready' | 'done'

export function stageOf(order: Order): ServiceStage {
  if (order.doneAt) return 'done'
  if (order.readyAt) return 'ready'
  return 'making'
}

/** Bếp báo xong. Bấm lại lần nữa là gỡ dấu — người bán bấm nhầm thì sửa được ngay. */
export async function markReady(orderId: string, ready = true): Promise<void> {
  await db.orders.update(orderId, {
    readyAt: ready ? new Date().toISOString() : null,
    synced: 0,
  })
}

/**
 * Giao tận tay khách, hoặc dọn bàn.
 *
 * Đơn chưa thu tiền thì không cho đánh dấu: giao đồ mà chưa lấy tiền là mất tiền, và
 * chỗ này không phải chỗ để quyết định điều đó.
 */
export async function markDone(orderId: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status !== 'Paid') return

  const now = new Date().toISOString()
  await db.orders.update(orderId, {
    readyAt: order.readyAt ?? now,
    doneAt: now,
    synced: 0,
  })
}

/** Ba số cuối của mã đơn — con số nhân viên gọi to khi chưa biết tên khách. */
export const callNumber = (code: string): string => code.slice(-3)

/** Ghi chú cho cả đơn: "khách quen", "ngồi ngoài hiên", "chờ bạn tới rồi mới lên món". */
export async function setOrderNote(orderId: string, note: string | null): Promise<void> {
  const clean = note?.trim()
  await db.orders.update(orderId, { note: clean ? clean.slice(0, 250) : null, synced: 0 })
}

/**
 * Bớt tiền cho hoá đơn. Truyền `null` là bỏ giảm giá.
 *
 * Một chỗ duy nhất ghi bốn ô giảm giá, để không bao giờ có cảnh còn cái nhãn "Giảm 10%"
 * trên bill mà số tiền đã bị gỡ ra.
 */
export async function applyDiscount(
  orderId: string,
  d: { amount?: Vnd; percent?: number; label: string; promotionId?: string | null } | null,
): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status !== 'Open') return

  const percent = d?.percent ?? 0
  const discount = d ? resolveDiscount(order.subtotal, d.amount ?? 0, percent) : 0
  const permille = await shopServicePermille()
  const serviceFee = serviceFeeOf(order.subtotal, discount, permille)

  await db.orders.update(orderId, {
    discount,
    discountLabel: d && discount > 0 ? d.label : null,
    discountPercent: discount > 0 ? percent : 0,
    promotionId: discount > 0 ? (d?.promotionId ?? null) : null,
    serviceFee,
    serviceFeePermille: permille,
    total: order.subtotal - discount + serviceFee,
    synced: 0,
  })
}

export function addToItems(items: OrderItem[], p: Product, option: string | null): OrderItem[] {
  const key = (i: OrderItem) => `${i.productId}|${i.optionLabel ?? ''}`
  const target = `${p.id}|${option ?? ''}`
  const found = items.find((i) => key(i) === target)

  if (found) {
    return items.map((i) =>
      key(i) === target
        ? { ...i, qtyMilli: i.qtyMilli + QTY_UNIT, lineTotal: lineTotal(i.unitPrice, i.qtyMilli + QTY_UNIT) }
        : i,
    )
  }

  return [
    ...items,
    {
      id: crypto.randomUUID(),
      productId: p.id,
      name: p.name,
      unitPrice: p.price,
      qtyMilli: QTY_UNIT,
      lineTotal: p.price,
      optionLabel: option,
      note: null,
    },
  ]
}

export function changeQty(items: OrderItem[], itemId: string, deltaMilli: number): OrderItem[] {
  return items
    .map((i) => {
      if (i.id !== itemId) return i
      const qty = i.qtyMilli + deltaMilli
      return { ...i, qtyMilli: qty, lineTotal: lineTotal(i.unitPrice, qty) }
    })
    .filter((i) => i.qtyMilli > 0)
}

/**
 * Quán thu tiền khi khách ra về, hay ngay lúc gọi món.
 *
 * Đọc từ gói thông tin quán nằm sẵn trên máy — thu tiền phải chạy được lúc mất mạng.
 */
async function shopPaysLater(): Promise<boolean> {
  const shop = await kvGet<{ payLater?: boolean }>('shop')
  return shop?.payLater === true
}

export async function payOrder(
  orderId: string,
  method: PayMethod,
  amountGiven: Vnd,
): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order) return

  const now = new Date().toISOString()

  /*
   * Thu tiền xong thì bàn trống ngay, hay vẫn bận?
   *
   * Quán ăn thu tiền lúc khách đứng dậy đi về — trả tiền là dấu chấm hết của lượt khách,
   * nên đóng luôn, khỏi bắt nhân viên bấm thêm một cái dọn bàn cho cái bàn đã trống.
   *
   * Quán cà phê và quầy mang về thu trước rồi khách mới ngồi hoặc mới đứng đợi — ở đó
   * trả tiền là lúc bàn BẮT ĐẦU bận, nên để ngỏ cho tới khi có người bấm dọn.
   */
  const later = await shopPaysLater()

  await db.orders.update(orderId, {
    status: 'Paid',
    paidAt: now,
    paymentMethod: method,
    amountGiven,
    changeGiven: method === 'Cash' ? Math.max(0, amountGiven - order.total) : 0,
    readyAt: later ? (order.readyAt ?? now) : order.readyAt,
    doneAt: later ? now : order.doneAt,
    synced: 0,
  })
}

/**
 * Ghi kết quả hoá đơn điện tử máy chủ trả về.
 *
 * Không đụng tới cờ `synced`: đây là dữ liệu đi <b>xuống</b>, đánh dấu chưa đồng bộ là đẩy
 * ngược một đơn đã thanh toán lên lại, mà đơn đã thanh toán thì bất biến.
 */
export async function saveEInvoice(orderId: string, einvoice: OrderEInvoice): Promise<void> {
  await db.orders.update(orderId, { einvoice })
}

// ───────────────────────── khuyến mãi ─────────────────────────

/** Ngày hôm nay theo lịch máy, dạng YYYY-MM-DD — cùng dạng với ngày chạy chương trình. */
const todayIso = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
}

/** Chương trình này bớt được bao nhiêu cho hoá đơn ngần này. 0 nghĩa là chưa đủ điều kiện. */
export function promotionDiscount(p: Promotion, subtotal: Vnd): Vnd {
  if (subtotal <= 0 || subtotal < p.minOrderTotal) return 0

  let raw = p.kind === 'Percent' ? Math.round((subtotal * p.value) / 100) : p.value
  if (p.kind === 'Percent' && p.maxDiscount > 0) raw = Math.min(raw, p.maxDiscount)

  return Math.min(Math.max(0, raw), subtotal)
}

/**
 * Chương trình đang chạy hôm nay, đã xếp theo thứ tự chủ quán đặt.
 *
 * Trả về cả chương trình chưa đủ điều kiện, kèm số tiền bớt bằng 0: người bán cần thấy
 * "hoá đơn từ 100k mới được giảm" chứ không phải thấy nút biến mất mà không hiểu vì sao.
 */
export async function runningPromotions(subtotal: Vnd): Promise<{ promo: Promotion; discount: Vnd }[]> {
  const today = todayIso()
  const all = await db.promotions.orderBy('sortOrder').toArray()

  return all
    .filter((p) => (!p.startsOn || today >= p.startsOn) && (!p.endsOn || today <= p.endsOn))
    .map((promo) => ({ promo, discount: promotionDiscount(promo, subtotal) }))
}

// ───────────────────────── sổ nợ khách quen ─────────────────────────

/**
 * Số tiền một khách còn nợ.
 *
 * Tổng đơn ghi sổ chưa huỷ, trừ tổng tiền khách đã trả. Tính lại mỗi lần đọc chứ không giữ
 * sẵn: giữ sẵn thì phải nhớ cập nhật ở năm chỗ — bán thêm, huỷ đơn, thu nợ, đồng bộ từ máy
 * khác — và quên một chỗ là con số ấy nói dối mãi mà không ai phát hiện.
 *
 * Đọc thẳng từ máy nên mất mạng vẫn tra được, đúng lúc khách đứng trước mặt hỏi "tôi còn
 * nợ bao nhiêu".
 */
export async function debtOf(customerId: string): Promise<Vnd> {
  const [orders, payments] = await Promise.all([
    db.orders.where('status').equals('Paid').toArray(),
    db.debtPayments.where('customerId').equals(customerId).toArray(),
  ])

  const owed = orders
    .filter((o) => o.customerId === customerId && o.paymentMethod === 'Debt')
    .reduce((s, o) => s + o.total, 0)

  return owed - payments.reduce((s, p) => s + p.amount, 0)
}

/** Số dư của mọi khách cùng một lượt — màn hình danh sách cần đúng thứ này. */
export async function debtByCustomer(): Promise<Map<string, Vnd>> {
  const [orders, payments] = await Promise.all([
    db.orders.where('status').equals('Paid').toArray(),
    db.debtPayments.toArray(),
  ])

  const out = new Map<string, Vnd>()
  for (const o of orders) {
    if (o.paymentMethod !== 'Debt' || !o.customerId) continue
    out.set(o.customerId, (out.get(o.customerId) ?? 0) + o.total)
  }
  for (const p of payments) {
    out.set(p.customerId, (out.get(p.customerId) ?? 0) - p.amount)
  }
  return out
}

/** Mấy đơn ghi sổ của một khách, mới nhất lên trước. */
export async function debtOrdersOf(customerId: string): Promise<Order[]> {
  const rows = await db.orders.where('status').equals('Paid').toArray()
  return rows
    .filter((o) => o.customerId === customerId && o.paymentMethod === 'Debt')
    .sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''))
}

/**
 * Khách trả bớt nợ.
 *
 * Ghi vào máy rồi đánh dấu chưa đồng bộ, y như một đơn hàng: tiền đã nằm trong tay người
 * bán rồi, không đợi mạng mới được ghi nhận.
 */
export async function payDebt(
  customerId: string,
  amount: Vnd,
  method: PayMethod = 'Cash',
  note: string | null = null,
): Promise<void> {
  if (amount <= 0) return

  await db.debtPayments.add({
    id: crypto.randomUUID(),
    customerId,
    amount,
    method,
    paidAt: new Date().toISOString(),
    note,
    deviceId: await deviceId(),
    synced: 0,
  })
}

/** Đóng đơn bằng cách ghi sổ cho một khách quen. */
export async function setOrderCustomer(
  orderId: string,
  customerId: string | null,
): Promise<void> {
  const customer = customerId ? await db.customers.get(customerId) : null
  await db.orders.update(orderId, {
    customerId: customer?.id ?? null,
    customerName: customer?.name ?? null,
    synced: 0,
  })
}

/** Huỷ đơn chưa thanh toán — khách đổi ý trước khi trả tiền. */
export async function voidOrder(orderId: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.status === 'Paid') return
  await db.orders.update(orderId, { status: 'Void', synced: 0 })
}

/**
 * Huỷ hoá đơn <b>đã thu tiền</b> — rung nhầm bill, khách trả lại hàng.
 *
 * Không xoá dòng và không sửa một con số nào: chỉ đánh dấu đã huỷ kèm lý do và giờ huỷ.
 * Xoá mất một hoá đơn là mất luôn cách chứng minh mình đúng khi khách khiếu nại.
 * Làm được cả lúc mất mạng; máy chủ nhận đúng một chuyển trạng thái này.
 */
export async function voidPaidOrder(orderId: string, reason: string): Promise<void> {
  const order = await db.orders.get(orderId)
  if (!order || order.voidedAt) return

  await db.orders.update(orderId, {
    status: 'Void',
    voidedAt: new Date().toISOString(),
    voidReason: reason.trim(),
    synced: 0,
  })
}

/** Hoá đơn trong một ngày, đọc thẳng từ máy nên mất mạng vẫn tra được. */
export async function ordersOnDay(day: Date): Promise<Order[]> {
  const start = new Date(day)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start.getTime() + 86_400_000)

  return db.orders
    .where('paidAt')
    .between(start.toISOString(), end.toISOString(), true, false)
    .reverse()
    .sortBy('paidAt')
}

/** Chuyển bàn: giữ nguyên đơn, đổi chỗ ngồi. Dùng khi khách đổi chỗ giữa chừng. */
export async function moveOrder(orderId: string, to: TableRef): Promise<void> {
  await db.orders.update(orderId, { tableId: to.id, tableName: to.name, synced: 0 })
}

/** Gộp bàn: dồn món của bàn nguồn sang bàn đích rồi đóng đơn nguồn. */
export async function mergeOrders(fromId: string, intoId: string): Promise<void> {
  const [from, into] = await Promise.all([db.orders.get(fromId), db.orders.get(intoId)])
  if (!from || !into || from.status !== 'Open' || into.status !== 'Open') return

  const items = [...into.items, ...from.items]
  await saveItems(intoId, items)

  // Hai nhóm ngồi chung một bàn thì số khách cũng cộng lại — bàn đích vừa nhận thêm người
  // chứ không phải vừa nhận thêm mấy ly nước.
  const guests = (into.guests ?? 0) + (from.guests ?? 0)
  if (guests > 0) await db.orders.update(intoId, { guests, synced: 0 })

  await db.orders.update(fromId, { status: 'Void', note: `Gộp vào ${into.tableName ?? into.code}`, synced: 0 })
}

// ───────────────────────── chi ─────────────────────────

export async function addExpense(
  cat: { id: string | null; name: string; emoji: string },
  amount: Vnd,
  note?: string,
  paymentMethod: PayMethod = 'Cash',
): Promise<void> {
  await db.expenses.add({
    id: crypto.randomUUID(),
    categoryId: cat.id,
    categoryName: cat.name,
    emoji: cat.emoji,
    amount,
    note: note ?? null,
    photoUrl: null,
    spentAt: new Date().toISOString(),
    deviceId: await deviceId(),
    paymentMethod,
    synced: 0,
  })
}

// ───────────────────────── ca làm ─────────────────────────

export interface ShiftTally {
  cashSales: Vnd
  cashExpense: Vnd
  transferSales: Vnd
  orderCount: number
  expectedCash: Vnd
}

/**
 * Ca đang mở của quán.
 *
 * Sắp xếp theo giờ mở rồi lấy cái sớm nhất, không lấy bừa: nếu hai máy lỡ cùng mở ca
 * trong lúc chưa kéo được dữ liệu của nhau, cả hai phải chọn ra CÙNG một ca — không thì
 * mỗi máy tính một số tiền phải có trong két.
 */
export async function currentShift(): Promise<Shift | undefined> {
  const open = await db.shifts.filter((s) => s.closedAt === null).toArray()
  return open.sort((a, b) => a.openedAt.localeCompare(b.openedAt))[0]
}

export async function openShift(openingCash: Vnd): Promise<Shift> {
  const already = await currentShift()
  if (already) return already

  const shift: Shift = {
    id: crypto.randomUUID(),
    openedAt: new Date().toISOString(),
    closedAt: null,
    openingCash,
    cashSales: 0,
    cashExpense: 0,
    expectedCash: openingCash,
    countedCash: 0,
    cashDiff: 0,
    note: null,
    deviceId: await deviceId(),
    synced: 0,
  }
  await db.shifts.add(shift)
  return shift
}

/**
 * Tính lại số liệu của ca từ dữ liệu trên máy này.
 *
 * Tính theo khoảng thời gian ca mở chứ không gắn khoá ca vào từng đơn — làm vậy thì
 * đơn bán lúc mất mạng vẫn rơi đúng ca mà không cần biết ca nào đang mở.
 */
export async function tallyShift(shift: Shift): Promise<ShiftTally> {
  const from = shift.openedAt
  const to = shift.closedAt ?? new Date().toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, true)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const spends = await db.expenses.where('spentAt').between(from, to, true, true).toArray()

  const cashSales = paid.filter((o) => o.paymentMethod === 'Cash').reduce((s, o) => s + o.total, 0)
  const transferSales = paid
    .filter((o) => o.paymentMethod === 'Transfer')
    .reduce((s, o) => s + o.total, 0)

  // Phiếu chi cũ chưa có trường này thì coi như tiền mặt — đúng với thực tế đi chợ.
  const cashExpense = spends
    .filter((e) => (e.paymentMethod ?? 'Cash') === 'Cash')
    .reduce((s, e) => s + e.amount, 0)

  return {
    cashSales,
    cashExpense,
    transferSales,
    orderCount: paid.length,
    expectedCash: shift.openingCash + cashSales - cashExpense,
  }
}

export async function closeShift(
  shift: Shift,
  countedCash: Vnd,
  note?: string,
): Promise<Shift> {
  const t = await tallyShift(shift)

  const closed: Shift = {
    ...shift,
    closedAt: new Date().toISOString(),
    cashSales: t.cashSales,
    cashExpense: t.cashExpense,
    expectedCash: t.expectedCash,
    countedCash,
    cashDiff: countedCash - t.expectedCash,
    note: note ?? null,
    synced: 0,
  }
  await db.shifts.put(closed)
  return closed
}

// ───────────────────────── tổng hợp trong ngày ─────────────────────────

export interface DayTotals {
  revenue: Vnd
  expense: Vnd
  profit: Vnd
  orderCount: number
  cash: Vnd
  transfer: Vnd
  /** Bán được nhưng ghi sổ — tiền chưa vào két. */
  debt: Vnd
}

const startOfDay = (d = new Date()) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export async function dayTotals(day: Date = new Date()): Promise<DayTotals> {
  const from = startOfDay(day).toISOString()
  const to = new Date(startOfDay(day).getTime() + 86_400_000).toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, false)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const spends = await db.expenses.where('spentAt').between(from, to, true, false).toArray()

  const revenue = paid.reduce((s, o) => s + o.total, 0)
  const expense = spends.reduce((s, e) => s + e.amount, 0)

  return {
    revenue,
    expense,
    profit: revenue - expense,
    orderCount: paid.length,
    cash: paid.filter((o) => o.paymentMethod === 'Cash').reduce((s, o) => s + o.total, 0),
    transfer: paid.filter((o) => o.paymentMethod === 'Transfer').reduce((s, o) => s + o.total, 0),
    // Bán được nhưng tiền chưa vào két. Tách hẳn ra để ba cột cộng lại đúng bằng doanh thu,
    // và để chủ quán thấy hôm nay bán bao nhiêu là ghi sổ.
    debt: paid.filter((o) => o.paymentMethod === 'Debt').reduce((s, o) => s + o.total, 0),
  }
}

export async function topProductsToday(limit = 6) {
  const from = startOfDay().toISOString()
  const to = new Date(startOfDay().getTime() + 86_400_000).toISOString()

  const paid = await db.orders
    .where('paidAt')
    .between(from, to, true, false)
    .filter((o) => o.status === 'Paid')
    .toArray()

  const acc = new Map<string, { name: string; qtyMilli: number; revenue: Vnd }>()
  for (const order of paid) {
    for (const item of order.items) {
      const row = acc.get(item.name) ?? { name: item.name, qtyMilli: 0, revenue: 0 }
      row.qtyMilli += item.qtyMilli
      row.revenue += item.lineTotal
      acc.set(item.name, row)
    }
  }

  return [...acc.values()].sort((a, b) => b.revenue - a.revenue).slice(0, limit)
}

/**
 * Tra sản phẩm theo mã vừa quét. Tra trên máy chứ không hỏi máy chủ: máy scan bắn
 * mã giữa lúc đông khách, đợi mạng là mất nhịp — và quán mất mạng vẫn phải quét được.
 */
export async function findByBarcode(code: string): Promise<Product | undefined> {
  return db.products.where('barcode').equals(code).first()
}

/** Xoá sạch dữ liệu máy này — dùng khi đăng xuất, hoặc khi sang tay quán cho người khác. */
export async function wipeLocal(): Promise<void> {
  await Promise.all([
    db.kv.clear(),
    db.categories.clear(),
    db.products.clear(),
    db.zones.clear(),
    db.diningTables.clear(),
    db.expenseCategories.clear(),
    db.promotions.clear(),
    db.optionSets.clear(),
    db.orders.clear(),
    db.expenses.clear(),
    db.shifts.clear(),
    db.customers.clear(),
    db.debtPayments.clear(),
  ])
}
